import { Button } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { HeaderBar, Page, SectionHeader } from "@dash/page";
import { useTheme } from "@emotion/react";
import React from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom-v5-compat";

import { useClientManager } from "@megaron/react-clients";

import { ImapConnectForm } from "../settings/ImapConnectForm";

const listPath = "/crm/zgloszenia";

type MailboxValues = {
  email: string;
  imapHost: string;
  imapPort: number;
  imapTls: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpTls: boolean;
  username: string;
};

type ActiveForm =
  | { mode: "add" }
  | { mode: "edit"; values: MailboxValues };

export const SupportSettingsPage = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const support = useClientManager("support");

  const connectionStatusQuery = support.imapConnectionStatus().useQuery({});
  const disconnectMutation = support.imapDisconnect().useMutation({
    onSuccess: () => queryClient.invalidateQueries({ predicate: () => true }),
  });
  const syncMutation = support.imapSync().useMutation({
    onSuccess: () => queryClient.invalidateQueries({ predicate: () => true }),
  });

  const [activeForm, setActiveForm] = React.useState<ActiveForm | null>(null);

  React.useEffect(() => {
    document.title = "Ustawienia";
  }, []);

  const mailboxes = connectionStatusQuery.data?.mailboxes ?? [];

  const handleFormSuccess = () => {
    setActiveForm(null);
    queryClient.invalidateQueries({ predicate: () => true });
  };

  return (
    <>
      <HeaderBar header="Ustawienia" onGoBack={() => navigate(listPath)} />
      <Page
        css={{
          marginTop: isMobile ? "53px" : "83px",
          padding: isMobile ? "0 1rem" : "0 2rem",
          marginBottom: "2rem",
        }}
      >
        <section css={{ marginTop: "1.5rem" }}>
          <SectionHeader children="Skrzynki pocztowe (IMAP)" />
          <p
            css={{
              marginTop: "0.5rem",
              marginBottom: "1rem",
              fontSize: "0.9375rem",
              color: theme.colors.secondaryText,
              maxWidth: "32rem",
            }}
          >
            Dodaj skrzynki IMAP, aby importować wiadomości do zgłoszeń. Działa z dowolnym dostawcą poczty (Gmail,
            home.pl, itp.).
          </p>
          {connectionStatusQuery.isLoading && <p css={{ color: theme.colors.secondaryText }}>Ładowanie…</p>}
          {mailboxes.length > 0 && (
            <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1rem" }}>
              {mailboxes.map((mb) => (
                <div key={mb.email}>
                  {activeForm?.mode === "edit" && activeForm.values.email === mb.email ? (
                    <ImapConnectForm
                      initialValues={activeForm.values}
                      onSuccess={handleFormSuccess}
                      onCancel={() => setActiveForm(null)}
                    />
                  ) : (
                    <div css={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                      <div css={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <span css={{ fontSize: "0.9375rem", fontWeight: 600 }}>{mb.email}</span>
                        <span css={{ color: theme.colors.secondaryText, fontSize: "0.8125rem" }}>
                          IMAP: {mb.imapHost}:{mb.imapPort} ({mb.imapTls ? "TLS" : "bez TLS"})
                          {"  ·  "}
                          SMTP: {mb.smtpHost}:{mb.smtpPort} ({mb.smtpTls ? "TLS" : "bez TLS"})
                        </span>
                      </div>
                      <Button
                        size="small"
                        variant="outline"
                        isDisabled={!!activeForm}
                        onClick={() =>
                          setActiveForm({
                            mode: "edit",
                            values: {
                              email: mb.email,
                              imapHost: mb.imapHost,
                              imapPort: mb.imapPort,
                              imapTls: mb.imapTls,
                              smtpHost: mb.smtpHost,
                              smtpPort: mb.smtpPort,
                              smtpTls: mb.smtpTls,
                              username: mb.username,
                            },
                          })
                        }
                      >
                        Edytuj
                      </Button>
                      <Button
                        size="small"
                        variant="outline"
                        isLoading={disconnectMutation.isLoading}
                        isDisabled={disconnectMutation.isLoading || !!activeForm}
                        onClick={() => disconnectMutation.mutate({ email: mb.email })}
                      >
                        Rozłącz
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {activeForm?.mode === "add" ? (
            <ImapConnectForm onSuccess={handleFormSuccess} onCancel={() => setActiveForm(null)} />
          ) : (
            !activeForm && (
              <Button
                size="small"
                variant="primary"
                onClick={() => setActiveForm({ mode: "add" })}
                css={{ alignSelf: "flex-start" }}
              >
                {mailboxes.length > 0 ? "Dodaj kolejną skrzynkę" : "Dodaj skrzynkę IMAP"}
              </Button>
            )
          )}
        </section>
        <section css={{ marginTop: "2rem" }}>
          <SectionHeader children="Synchronizacja" />
          <p
            css={{
              marginTop: "0.5rem",
              marginBottom: "1rem",
              fontSize: "0.9375rem",
              color: theme.colors.secondaryText,
              maxWidth: "32rem",
            }}
          >
            Pobierz wiadomości ze wszystkich podpiętych skrzynek IMAP (z ostatniego dnia).
          </p>
          <div css={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outline"
              isLoading={syncMutation.isLoading}
              isDisabled={syncMutation.isLoading}
              onClick={() => syncMutation.mutate({})}
            >
              {syncMutation.isLoading ? "Synchronizuję…" : "Synchronizuj"}
            </Button>
            {syncMutation.isError && (
              <span css={{ fontSize: "0.875rem", color: theme.colors.danger }}>
                Synchronizacja nie powiodła się. Sprawdź logi backendu.
              </span>
            )}
            {syncMutation.isSuccess && syncMutation.data && (
              <span css={{ fontSize: "0.875rem", color: theme.colors.primary }}>
                Zsynchronizowano
                {typeof syncMutation.data.messageCount === "number" &&
                  ` (${syncMutation.data.messageCount} wiadomości)`}
              </span>
            )}
          </div>
        </section>
      </Page>
    </>
  );
};
